import {
    ButtonFlavor,
    ButtonSize,
    ButtonType,
    ExButton,
    ExEmptyState,
    ExIcon,
    ExInput,
    SearchFlavor,
    SearchSize,
} from '@boomi/exosphere';
import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { useApp } from '../common/AppProvider';
import IconCircle from '../common/IconCircle';
import { translations } from '../translations/translations';
import type { Action, AuthenticationStatusResponse, Trigger } from '../types/types';
import { getAuthenticatedConnectors, getUniqueConnectors } from '../utils/utils';

import './connector-picker.css';

const ConnectorPicker = ({
    itemType,
    itemData,
    authenticationStatus,
    onConnectorChange,
    onCancel,
}: {
    itemType: 'action' | 'trigger';
    itemData: Trigger | Action | null;
    authenticationStatus: AuthenticationStatusResponse;
    onConnectorChange: (id: string) => void;
    onCancel: () => void;
}) => {
    const { connectors, language } = useApp();
    const { providerId = null } = itemData ?? {};

    const [search, setSearch] = useState('');
    const [selectedConnectorId, setSelectedConnectorId] = useState<string | null>(providerId);

    // Do we have a selected connector? We should do when changing the connector on an existing
    // trigger/action or if a connector has been just selected from the list, otherwise not.
    const selectedConnector = providerId
        ? connectors[providerId]
        : selectedConnectorId
          ? connectors[selectedConnectorId]
          : null;

    // Filter connectors by itemType (only connectors that have triggers or
    // only connectors that have actions).
    const itemTypeConnectors = useMemo(
        () =>
            Object.values(connectors).filter((connector) => {
                return itemType === 'trigger'
                    ? Object.values(connector.triggers).length
                    : Object.values(connector.actions).length;
            }),
        [connectors, itemType],
    );

    // Filter connectors that match/contain the search term.
    const filteredConnectors = itemTypeConnectors.filter((item) =>
        item.displayName.toUpperCase().includes(search.toUpperCase()),
    );

    // Always show any connectors that have already been selected.
    // A selected connector will stay at original position if no filtering is applied,
    // otherwise it will appear in regular alphabetical order.
    // If sorting is false the selected connector will appear at the start of the list.
    const listConnectors =
        selectedConnector && !!search
            ? getUniqueConnectors([selectedConnector].concat(filteredConnectors))
            : filteredConnectors;

    // Check authentication status for all relevant connectors (itemTypeConnectors),
    // so that we can indicate which ones are fully authenticated.
    const fullyAuthenticatedConnectors = useMemo(
        () => getAuthenticatedConnectors(connectors, itemType, authenticationStatus),
        [connectors, itemType, authenticationStatus],
    );

    return (
        <>
            <div className="drawer-content">
                <section className="drawer-connector-picker">
                    <p className="text md">
                        {itemType === 'trigger'
                            ? translations[language].TASK_when_hint
                            : translations[language].TASK_then_hint}
                    </p>

                    <div className="search-panel">
                        <ExInput
                            data-testId="connectorPickerSearch"
                            className="exo-search-input"
                            placeholder={translations[language].COMMON_search_placeholder}
                            input-id="default"
                            type="text"
                            inputmode="text"
                            size={SearchSize.MEDIUM}
                            flavor={SearchFlavor.WHITE}
                            leadingIcon="magnifying-glass"
                            onChange={(e: CustomEvent) =>
                                setSearch((e.currentTarget as HTMLInputElement).value)
                            }
                            autoComplete="off"
                            clearable
                        />
                    </div>

                    <div className="connector-picker-container">
                        {listConnectors.length ? (
                            <ul className="connector-picker-list" data-testid="connectorPickerList">
                                {listConnectors.map((connector) => (
                                    <li key={connector.id}>
                                        <button
                                            type="button"
                                            className={classNames('app-tile', {
                                                'is-selected': selectedConnectorId === connector.id,
                                            })}
                                            title={connector.description}
                                            onClick={() => setSelectedConnectorId(connector.id)}
                                        >
                                            <IconCircle
                                                connector={connector}
                                                showAuthBadge={fullyAuthenticatedConnectors.includes(
                                                    connector.id,
                                                )}
                                                language={language}
                                            />
                                            <span className="app-tile-text">
                                                {connector.displayName}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <ExEmptyState
                                className="empty-state"
                                label={translations[language].COMMON_no_results}
                                text={translations[language].COMMON_search_hint}
                            >
                                <ExIcon
                                    className="empty-state-icon"
                                    slot="header"
                                    icon="community"
                                />
                            </ExEmptyState>
                        )}
                    </div>
                </section>
            </div>
            <footer className="drawer-footer">
                <ExButton
                    className="exo-button"
                    flavor={ButtonFlavor.BASE}
                    size={ButtonSize.DEFAULT}
                    type={ButtonType.SECONDARY}
                    onClick={onCancel}
                >
                    {translations[language].COMMON_cancel}
                </ExButton>
                <ExButton
                    data-testid="connectorPickerConfirm"
                    className="exo-button"
                    flavor={ButtonFlavor.BASE}
                    size={ButtonSize.DEFAULT}
                    type={ButtonType.PRIMARY}
                    disabled={!selectedConnectorId}
                    onClick={() => onConnectorChange(selectedConnectorId!)}
                >
                    {translations[language].COMMON_confirm}
                </ExButton>
            </footer>
        </>
    );
};
export default ConnectorPicker;
